import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { patchPreferences } from '@/api/me';

const SERVER_KEY = 'savedTablePreferences';
const LS_KEY = 'v2TablePrefs';

export interface TablePrefs {
  vis?: Record<string, boolean>;
  size?: Record<string, number>;
  order?: string[];
  pinning?: { left?: string[]; right?: string[] };
  density?: string;
  gridlines?: boolean;
  sorting?: string | undefined;
  filters?: string | undefined;
  groupBy?: string | null | undefined;
  rowsPerPage?: number;
}

type ServerEntry = { table: string; [k: string]: unknown };

function lsKey(storageKey: string, field: string) {
  return `${storageKey}_${field}`;
}

function writeLs(storageKey: string, field: string, value: unknown) {
  try {
    localStorage.setItem(lsKey(storageKey, field), JSON.stringify(value));
  } catch {}
}

function getAllPrefs(): Record<string, TablePrefs> {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v ? (JSON.parse(v) as Record<string, TablePrefs>) : {};
  } catch {
    return {};
  }
}

function setAllPrefs(data: Record<string, TablePrefs>) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {}
}

function getServerArray(): ServerEntry[] {
  try {
    const v = localStorage.getItem(`${LS_KEY}_serverArray`);
    return v ? (JSON.parse(v) as ServerEntry[]) : [];
  } catch {
    return [];
  }
}

function setServerArray(arr: ServerEntry[]) {
  try {
    localStorage.setItem(`${LS_KEY}_serverArray`, JSON.stringify(arr));
  } catch {}
}

export function getTablePrefs(storageKey: string): TablePrefs {
  return getAllPrefs()[storageKey] ?? {};
}

export function clearTablePreferences(): void {
  const all = getAllPrefs();
  for (const tableKey of Object.keys(all)) {
    for (const field of [
      'vis',
      'size',
      'order',
      'pinning',
      'density',
      'gridlines',
    ]) {
      try {
        localStorage.removeItem(lsKey(tableKey, field));
      } catch {}
    }
  }
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(`${LS_KEY}_serverArray`);
  } catch {}
}

export function seedTablePreferencesFromSession(
  preferences: Record<string, unknown>,
) {
  const serverPrefs = preferences[SERVER_KEY];
  if (!serverPrefs || !Array.isArray(serverPrefs)) return;

  const arr = serverPrefs as ServerEntry[];
  setServerArray(arr);

  // Extract v2-specific fields (sorting, filters) into our localStorage map
  const map: Record<string, TablePrefs> = getAllPrefs();
  for (const entry of arr) {
    const {
      table,
      sorting,
      filters,
      vis,
      size,
      order,
      density,
      gridlines,
      rowsPerPage,
    } = entry as ServerEntry & TablePrefs;
    if (!table) continue;
    map[table] = {
      ...map[table],
      ...(sorting !== undefined ? { sorting: sorting as string } : {}),
      ...(filters !== undefined ? { filters: filters as string } : {}),
      ...(rowsPerPage !== undefined ? { rowsPerPage } : {}),
    };
    if (vis !== undefined) writeLs(table, 'vis', vis);
    if (size !== undefined) writeLs(table, 'size', size);
    if (order !== undefined) writeLs(table, 'order', order);
    if ((entry as TablePrefs).pinning !== undefined)
      writeLs(table, 'pinning', (entry as TablePrefs).pinning);
    if (density !== undefined) writeLs(table, 'density', density);
    if (gridlines !== undefined) writeLs(table, 'gridlines', gridlines);
  }
  setAllPrefs(map);
}

export function useTablePreferences(storageKey: string) {
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readyRef = useRef(false);

  // Note: seedTablePreferencesFromSession is called in __root.tsx beforeLoad,
  // so localStorage is already populated before any DataTable mounts.

  useEffect(() => {
    const t = setTimeout(() => {
      readyRef.current = true;
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const syncToBackend = useCallback((tableKey: string) => {
    if (!readyRef.current) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const arr = getServerArray();
      const idx = arr.findIndex(e => e.table === tableKey);
      const base: ServerEntry = arr[idx] ?? {
        table: tableKey,
        rowsPerPage: 50,
      };
      // Merge full stored prefs for this table so sorting + filters are both included
      const full = getAllPrefs()[tableKey] ?? {};
      const entry: ServerEntry = { ...base, ...full };
      if (idx >= 0) arr[idx] = entry;
      else arr.push(entry);
      setServerArray(arr);
      void patchPreferences(SERVER_KEY, arr);
    }, 1000);
  }, []);

  const save = useCallback(
    (prefs: TablePrefs) => {
      if (prefs.vis !== undefined) writeLs(storageKey, 'vis', prefs.vis);
      if (prefs.size !== undefined) writeLs(storageKey, 'size', prefs.size);
      if (prefs.order !== undefined) writeLs(storageKey, 'order', prefs.order);
      if (prefs.pinning !== undefined)
        writeLs(storageKey, 'pinning', prefs.pinning);
      if (prefs.density !== undefined)
        writeLs(storageKey, 'density', prefs.density);
      if (prefs.gridlines !== undefined)
        writeLs(storageKey, 'gridlines', prefs.gridlines);

      const all = getAllPrefs();
      all[storageKey] = { ...all[storageKey], ...prefs };
      setAllPrefs(all);

      syncToBackend(storageKey);
    },
    [storageKey, syncToBackend],
  );

  return { save };
}

/**
 * On first mount, restores saved sorting/filters from prefs into the URL
 * if the URL has neither. `mapPrefs` converts TablePrefs → partial search params.
 */
export function useRestorePrefsToSearch<
  TSearch extends Record<string, unknown>,
>(
  storageKey: string,
  opts: {
    hasExistingSearch: boolean;
    mapPrefs: (prefs: TablePrefs) => Partial<TSearch>;
    /** When false, the restore is skipped entirely. Used by `useTable` when
     *  it runs in `mode: 'state'` (no URL involvement). Defaults to true. */
    enabled?: boolean;
  },
) {
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount
  useEffect(() => {
    if (opts.enabled === false) return;
    if (restoredRef.current || opts.hasExistingSearch) return;
    restoredRef.current = true;
    const prefs = getTablePrefs(storageKey);
    const patch = opts.mapPrefs(prefs);
    if (Object.keys(patch).length === 0) return;
    navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
      replace: true,
    } as Parameters<typeof navigate>[0]);
  }, []);
}
